import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollManager = () => {
  const location = useLocation();
  const didMountRef = useRef(false);

  // Disable browser's automatic scroll restoration
  useEffect(() => {
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = prev;
    };
  }, []);

  useEffect(() => {
    const hash = location.hash?.slice(1);
    
    // If there's a hash, always scroll to it (even on initial mount)
    if (hash) {
      // Retry mechanism to wait for element to load (handles loading animations)
      let attempts = 0;
      const maxAttempts = 30; // 3 seconds total
      const retryInterval = 100; // ms
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      
      const tryScroll = () => {
        const el = document.getElementById(decodeURIComponent(hash));
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          const heading = el.querySelector('h1, h2, h3, [role="heading"]') as HTMLElement | null;
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: true });
          }
        } else if (attempts < maxAttempts) {
          attempts++;
          timeoutId = setTimeout(tryScroll, retryInterval);
        }
      };
    
    tryScroll();
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
    }

    // Only scroll to top if we've already mounted (not on initial page load)
    if (didMountRef.current) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    } else {
      didMountRef.current = true;
    }
  }, [location.pathname, location.hash]);

  return null;
};

export default ScrollManager;